/**
 * Create Service Route
 *
 * Form for creating new services
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { ServiceForm } from '../../components/services';
import { createService, ApiError, type CreateServiceInput } from '../../lib/apiClient';
import type { CreateServiceFormData } from '../../components/forms/validation';
import { usePermission } from '../../hooks/usePermission';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './new.css';

export const Route = createFileRoute('/services/new')({
  component: CreateServicePage,
});

function CreateServicePage(): JSX.Element {
  const navigate = useNavigate();
  const canCreate = usePermission('service:create');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (data: CreateServiceFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Convert form data to API input
      const input: CreateServiceInput = {
        name: data.name,
        shortName: data.shortName,
        description: data.description,
        serviceType: data.serviceType,
        teamId: data.teamId,
        repo: data.repo || undefined,
        lifecycleStatus: data.lifecycleStatus,
        tier: data.tier,
      };

      // Create service via REST API
      const service = await createService(input);

      // Navigate to the new service detail page
      navigate({ to: `/services/${service.entityId}` });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({
      to: '/services',
      search: {
        page: 1,
        pageSize: 25,
        search: '',
        lifecycleStatus: '',
        tier: '',
        tagId: '',
        teamId: '',
        technologyId: '',
      },
    });
  };

  // Gate the whole form on permission so users without service:create are
  // told up front (on load) instead of after filling out and submitting the
  // form, which previously wasted their time (issue #336).
  if (!canCreate) {
    return (
      <div className="create-service-page">
        {/* Breadcrumb Navigation */}
        <div className="breadcrumb">
          <Link
            to="/services"
            search={{
              page: 1,
              pageSize: 25,
              search: '',
              lifecycleStatus: '',
              tier: '',
              tagId: '',
              teamId: '',
              technologyId: '',
            }}
            className="breadcrumb-link"
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            <span>Back to Services</span>
          </Link>
        </div>

        <BannerElement
          theme="warning"
          dismissed={false}
          className="access-denied-banner"
          role="alert"
        >
          <div className="banner-content">
            <IconElement name="lock" aria-hidden="true" />
            <span>
              You do not have permission to create services. Contact your administrator if you need
              access.
            </span>
          </div>
        </BannerElement>
      </div>
    );
  }

  return (
    <div className="create-service-page">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb">
        <Link
          to="/services"
          search={{
            page: 1,
            pageSize: 25,
            search: '',
            lifecycleStatus: '',
            tier: '',
            tagId: '',
            teamId: '',
            technologyId: '',
          }}
          className="breadcrumb-link"
        >
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Services</span>
        </Link>
      </div>

      {/* Header */}
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Service</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new service to the catalog
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      {/* Service Form */}
      <ServiceForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

// Export for testing
export { CreateServicePage };
